<template>
  <!-- This is a unit select CARD for the slideout panel (at all levels) -->
  <!-- It may be a UNIT GROUP CARD or a SINGLE UNIT CARD -->
  <transition name="fade">

    <div
      data-structure="relationship"
      :class="[ 'unit-checkbox-card', structure ? 'group-unit' : 'single-unit', selected ? 'selected' : '' ]"
      v-if="showAll || selected" :id="`rel-${code}`">

      <!-- This load state overlays the whole slideout panel... -->
      <transition name="fade">
        <div class="overlay-state overlay-state-loading" v-if="!allChildrenLoaded">
          <div class="overlay-state__body">
            <i class="overlay-state__icon far fa-spinner-third fa-spin"></i>
            <div class="overlay-state__header">Applying unit selections</div>
            <div class="overlay-state__content">Please wait...</div>
          </div>
        </div>
      </transition>

      <div class="unit-checkbox-card__body">
        <div class="unit-checkbox-card__left">
          <div class="unit-checkbox-card__code">{{ code }}</div>
          <div class="unit-checkbox-card__main">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" value="" :id="`unit-${location}`" v-on:change="select" v-bind:checked="selected"
                :disabled="mandatory || loadingUnitDetail || (!available && !selected)">
              <label class="form-check-label" :for="`unit-${location}`">
                {{ name }}
              </label>
            </div>
          </div>
          <template v-if="loadingUnitDetail">
            <div class="overlay-state__header"><i class="overlay-state__icon far fa-spinner-third fa-spin"></i> Loading</div>
            <div class="overlay-state__content">Please wait...</div>
          </template>
          <template v-if="!loadingUnitDetail && available">
            <button type="button" class="unit-checkbox-card__more-info-btn" v-if="isUnit" v-on:click="viewUnit">View unit details</button>
          </template>
          <div v-if="duplicate" class="duplicate" :id="`tooltip-duplicate-so-${relationship.code}`">
            Duplicate
          </div>

        </div>
        <div class="unit-checkbox-card__right">

          <div class="unit-checkbox-card__credits" v-if="isUnit">{{ this.relationship.credits }} points</div>
          <span v-if="isReviewer && isUnit"><br/><div v-if="isDiscontinued || isArchived" class="badge badge-danger" :class="isDiscontinued ? 'mr-1' : ''">{{ statusLabel(this.relationship.masterStatus) }}</div><div v-else-if="showStatus" class="badge badge-primary" :class="this.relationship.status == 'Active' ? 'badge-success' : 'badge-primary'">{{ statusLabel(this.relationship.status) }}</div><div class="ml-1 badge badge-secondary">Version {{ this.relationship.courseLoopVersionNumber || this.relationship.version }}</div></span>

          <div class="unit-checkbox-card__credit-alert" v-if="!isUnit"
            :class="[ totalCredits(this.location) > 0 && totalCredits(this.location) < this.relationship.credits ? 'warning' : '', totalCredits(this.location) > this.relationship.credits ? 'danger' : '' ]">
            <span v-if="!isUnit && totalCredits(this.location) > 0 && totalCredits(this.location) < this.relationship.credits">
              <i class="far fa-exclamation-circle"></i>Select {{ this.relationship.credits - totalCredits(this.location) }} more points
            </span>
            <span v-if="!isUnit && totalCredits(this.location) > this.relationship.credits">
              <i class="far fa-exclamation-circle"></i>Too many units selected
            </span>
            <span v-if="!isUnit" class="unit-checkbox-card__credit-count" :class="totalCredits(this.location) > 0 ? 'highlight' : ''">
              {{ totalCredits(this.location) }} / {{ this.relationship.credits }} points
            </span>
            <span v-if="isReviewer"><br/><div v-if="isDiscontinued || isArchived" class="badge badge-danger mr-1">{{ statusLabel(this.relationship.masterStatus) }}</div><div v-if="showStatus" class="badge badge-primary" :class="this.relationship.status == 'Approved' ? 'badge-success' : 'badge-primary'">{{ statusLabel(this.relationship.status) }}</div><div class="ml-1 badge badge-secondary">Version {{ this.relationship.courseLoopVersionNumber }}</div></span>
          </div>

          <div class="unit-checkbox-card__complete" v-if="isUnit">
            <div v-if="duplicate && false" class="duplicate" :id="`tooltip-duplicate-so-${relationship.code}`">
              Duplicate
            </div>
            <button type="button" :class="['btn-icon', completed ? 'complete' : '']" v-on:click="toggleUnitComplete()"
              :id="`tooltip-completed-so-${relationship.code}`">
              <i class="fa-check-circle" :class="completed ? 'fas' : 'far'"></i>
              <span v-if="!completed">Mark unit as complete</span>
              <span v-if="completed">Unit completed</span>
            </button>
          </div>

        </div>

        <div class="unit-checkbox-card__rel-description" v-if="relationship.description.length">{{ relationship.description }}</div>

      </div>

      <!-- error lists go here -->
      <template v-if="error && selected">
        <div class="unit-details">
          <div class="page-content-state">
            <span class="page-content-state__icon  fal fa-sad-cry"></span>
            <div class="page-content-state__header">Sorry, there was a problem loading selection details.</div>
            <button type="button" class="btn btn-primary mt-3" v-on:click="updateState()">Try again</button>
          </div>
        </div>
      </template>
      <template v-else-if="error">
        <div class="unit-details">
          <div class="page-content-state">
            <span class="page-content-state__icon  fal fa-sad-cry"></span>
            <div class="page-content-state__header">Sorry, there was a problem loading unit details.</div>
          </div>
        </div>
      </template>

      <!-- Child groups go here... -->
      <div v-if="structure" class="unit-checkbox-card__sub-container">
        <!-- Display popup when selected IF 'elective needed' -->
        <SlideOutRelationshipElectiveAlert v-if="electiveNeeded && selected && !loading" :anchorCode="code" />
        <SlideOutStructure :structure="structure" :location="location" :mandatory="true" :firstLevelTitle="firstLevelTitle" :childStructureCode="code"
          v-on:structureLoaded="setChildrenLoaded()" />
      </div>

      <!-- Not available in location alert... -->
      <div v-if="!available" class="unit-card__alert-bottom" :class="selected ? 'warning' : 'info'">
        <span class="unit-card__alert-bottom-content">This unit is not available in your location.</span>
      </div>

      <!-- Alert state for Level 100 error (show IF selected & a l100 unit & too many l100 units selected... ) -->
      <div class="unit-checkbox-card__alert-banner l100" v-if="false">
        <span class="unit-checkbox-card__alert-banner-content">
          Level 100 unit
        </span>
      </div>

      <div class="unit-card__alert-bottom prereq" v-if="isUnit && isMissingRequirements">
        <span :id="`tooltip-prereq-alert-so-${relationship.code}`" class="unit-card__alert-bottom-content">
          Pre-requisite not selected <i class="far fa-info-circle fa-lg"></i>
        </span>
        <button type="button" class="unit-card__alert-bottom-action" v-on:click="viewUnit">See details</button>
      </div>

      <div class="unit-card__alert-bottom prereq" v-if="isUnit && hasExclusionClash">
        <span :id="`tooltip-prereq-alert-so-${relationship.code}`" class="unit-card__alert-bottom-content">
          {{ __("label:exclusion conflict") }} <i class="far fa-info-circle fa-lg"></i>
        </span>
        <button type="button" class="unit-card__alert-bottom-action" v-on:click="viewUnit">See details</button>
      </div>

      <div class="unit-card__alert-bottom prereq" v-if="isUnit && isNotActive && !isArchived && !isReviewer">
        <span :id="`tooltip-status-alert-so-${relationship.code}`" class="unit-card__alert-bottom-content">
          <i class="far fa-exclamation-triangle fa-lg mr-1"></i> Unit is not active! [{{unitStatus}}]
        </span>
      </div>

      <div class="unit-card__alert-bottom prereq" v-if="isArchived && !isReviewer">
        <span :id="`tooltip-status-alert-so-${relationship.code}`" class="unit-card__alert-bottom-content">
          <i class="far fa-exclamation-triangle fa-lg mr-1"></i> {{relationship.type}} is discontinued - contact the Academic Chair for more information.
        </span>
      </div>

      <b-tooltip v-if="duplicate" :target="`tooltip-duplicate-so-${relationship.code}`" triggers="hover focus" placement="right">
        {{ __("label:duplicate tooltip") }}
      </b-tooltip>
      <b-tooltip :target="`tooltip-completed-so-${relationship.code}`" triggers="hover" placement="left">
        <span v-html="__('label:completed tooltip')"></span>
      </b-tooltip>
      <b-tooltip v-if="isUnit && isMissingRequirements" :target="`tooltip-prereq-alert-so-${relationship.code}`" triggers="hover" placement="right"
        noninteractive>
        You have not selected the pre-requisite for this unit. Please add the pre-requisite unit to your unit selection through the General Electives component
        within Option.
      </b-tooltip>
      <b-tooltip v-if="isUnit && hasExclusionClash" :target="`tooltip-prereq-alert-so-${relationship.code}`" triggers="hover" placement="right"
        noninteractive>
        {{ __("info:exclusion conflict") }}
      </b-tooltip>

    </div>

  </transition>

</template>

<script>
import { mapMutations, mapActions, mapGetters } from 'vuex'
import { dynamicSort } from 'common/helpers/dynamic-sort.js'
import Curriculum from 'client/js/models/curriculum.js'
import creditChangeCalculator from 'client/js/utils/credit-change-calculator.js'
import inputBuilder from 'common/helpers/input-builder.js'
import preReqValidator from 'common/helpers/pre-req-validator.js'
import exclusionValidator from 'common/helpers/exclusion-validator.js'
import SlideOutRelationshipElectiveAlert from './SlideOutRelationshipElectiveAlert.vue'

export default {
  name: 'SlideOutRelationship',
  data: function () {
    return {
      credits: 0,
      item: this.relationship.structure ? { structure: [this.relationship.structure] } : null,
      loading: null,
      loadingUnitDetail: false,
      error: null,
      childrenLoaded: false,
      available: true
    }
  },
  props: {
    relationship: {
      type: Object,
      required: true
    },
    location: {
      type: String,
      required: true
    },
    mandatory: {
      type: Boolean,
      required: true
    },
    virtualRelationship: {
      type: Boolean,
      required: false,
      default: false
    },
    parentLocation: {
      type: String,
      required: false
    },
    firstLevelTitle: {
      type: String,
      required: true
    },
    showAll: {
      type: Boolean,
      required: false,
      default: true
    },
    verticalGrouping: {
      type: String,
      required: true
    }
  },
  created: async function () {
    if (this.mandatory && !this.selected) {
      this.add()
    } else if (this.selected) {
      await this.updateState()
    }
  },
  beforeDestroy() {
    //this.remove()
  },
  computed: {
    ...mapGetters({
      selectedPreferences: 'preferences/getSelectedStudyPreferences',

      items: 'units/getSelectedCurriculumItems',
      totalCredits: 'units/getTotalCredits',
      completedUnits: 'units/getCompletedUnits',
      selectedUnitCodes: 'units/getSelectedUnitCodes',
      includeDraftVersions: 'courses/includeDraftVersions',
      isReviewer: 'global/isReviewer'
    }),
    isVirtual() {
      return this.relationship.virtual || this.virtualRelationship
    },
    isUnit() {
      return this.type === 'Unit'
    },
    selected() {
      return this.items.filter((item) => item.id === this.relationship.id && item.location === this.location).length > 0
    },
    duplicate() {
      const result = this.items.filter((item) => item.id === this.relationship.id)
      return result && result.length > 1
    },
    completed() {
      return this.completedUnits.includes(this.relationship.code)
    },
    foundItem() {
      return this.items.find((item) => item.id === this.relationship.id && item.location === this.location)
    },
    code() {
      return this.relationship.code
    },
    name() {
      return this.relationship.name
    },
    type() {
      return this.relationship.type
    },
    structure() {
      if (this.item && this.item.structure) {
        return this.item.structure[0]
      } else {
        return null
      }
    },
    allChildrenLoaded() {
      if (this.loading == null) {
        return true
      } else if (!this.loading && !this.structure) {
        return true
      } else if (!this.loading && this.structure && this.childrenLoaded) {
        return true
      }
      return false
    },
    convertedPreReqs() {
      const result = this.items.find((item) => item.id === this.relationship.id)
      if (result && this.isUnit && result.relationship.unit) {
        return inputBuilder.convertRequisites(result.relationship.unit.requisites, "Prerequisite")
      }
      return null
    },
    convertedExclusions() {
      const result = this.items.find((item) => item.id === this.relationship.id)
      if (result && this.isUnit && result.relationship.unit) {
        return inputBuilder.convertRequisites(result.relationship.unit.requisites, "Exclusion")
      }
      return null
    },
    convertedEquivalents() {
      const result = this.items.find((item) => item.id === this.relationship.id)
      if (result && this.isUnit && result.relationship.unit) {
        return inputBuilder.convertRequisites(result.relationship.unit.requisites, "Equivalent")
      }
      return null
    },
    isMissingRequirements() {
      if (this.convertedPreReqs != null && this.isUnit) {
        return !preReqValidator.prerequisitesExistTest(this.relationship.code, this.selectedUnitCodes, this.convertedPreReqs)
      }
      return null
    },
    hasExclusionClash() {
      return this.isUnit &&
        (
           (this.convertedExclusions && exclusionValidator.exclusionClashTest(this.relationship.code, this.selectedUnitCodes, this.convertedExclusions)) // Exclusions
        || (this.convertedEquivalents && exclusionValidator.exclusionClashTest(this.relationship.code, this.selectedUnitCodes, this.convertedEquivalents)) // Equivalents
        )
    },
    isNotActive() {
      return this.isUnit && this.unitStatus && this.unitStatus != 'Active'
    },
    showStatus() {
      return this.relationship.status != this.relationship.masterStatus || !(this.isDiscontinued || this.isArchived)
    },
    isDiscontinued() {
      // console.log('isDiscontinued', this.relationship.masterStatus, this.isUnit, this.unitStatus)
      return this.relationship.masterStatus == 'Discontinued'
    },
    isArchived() {
      // console.log('isArchived', this.relationship.masterStatus, this.isUnit, this.unitStatus)
      return this.relationship.masterStatus == 'Archived'
    },
    unitStatus() {
      return this.isUnit && this.relationship?.unit?.status
    },
    electiveNeeded() {
      if (!this.isUnit && this.totalCredits(this.location) > 0 && this.totalCredits(this.location) < this.relationship.credits && !this.loading) {
        return true
      }
      return false
    }
  },
  methods: {
    // __:i18n,
    ...mapMutations('global', {
      lockSlideOut: 'lockSlideOut'
    }),
    ...mapActions({
      attachStructure: 'courses/attachStructure',
      detachStructure: 'courses/detachStructure',
      addRelationships: 'courses/addRelationships',
      removeRelationships: 'courses/removeRelationships',

      addItem: 'units/addCurriculumItem',
      removeItem: 'units/removeCurriculumItem',
      updateUnitCompletion: 'units/updateUnitCompletion'
    }),
    lockAndLoad: function (locked) {
      this.loading = locked
      //load the unit, and lock the slideout, until we finish loading.
      this.lockSlideOut(locked)
    },
    statusLabel(id) {
      return this.__(`label:status ${id.toLowerCase()}`)
    },
    select: async function () {
      this.$emit('clicked', this.relationship)
      if (!this.selected) {
        await this.add()
      } else {
        await this.remove()
      }
    },
    add: async function () {
      //adding lookup for units that have not yet been loaded from the curriculum
      if (this.type == 'Unit' && this.relationship.unit === undefined) {
        this.lockAndLoad(true)
        this.error = null
        try {
          this.relationship.unit = await Curriculum.getUnit(this.relationship.code, this.relationship.version, this.includeDraftVersions)
        } catch (err) {
          this.error = err
          this.lockAndLoad(false)
          return
        }
      }

      this.addItem({
        firstLevelTitle: this.firstLevelTitle,
        relationship: this.relationship,
        location: this.location,
        verticalGrouping: this.verticalGrouping
      })
      //console.log("adding "+this.relationship.id);
      await this.updateState()
      // If not a unit (relationship group) scroll to top to help display child units
      if (!this.isUnit) {
        var relId = 'rel-' + this.code
        var selectedRelGroup = document.getElementById(relId).offsetTop
        document.getElementById('slideOutBodyTree').scrollTop = selectedRelGroup
      }
    },
    remove: async function () {
      //console.log("removing "+this.relationship.id)
      this.error = null
      this.removeItem({
        firstLevelTitle: this.firstLevelTitle,
        location: this.location,
        relationship: {
          type: this.relationship.type,
          credits: this.credits
        },
        recursive: true
      })
      await this.updateState()
    },
    toggleUnitComplete() {
      this.updateUnitCompletion({
        id: this.relationship.id,
        code: this.relationship.code,
        completed: !this.completed
      })
      if (this.completed && !this.selected) {
        this.select()
      }
    },
    updateState: async function () {
      if (this.selected) {
        this.error = null
        try {
          this.lockAndLoad(true)
          let relationship = this.relationship
          // console.log('updateState relationship', relationship.name, relationship.type, relationship.id, relationship.code, relationship.version, relationship.versionNumber, relationship.courseLoopVersionNumber, relationship)
          switch (this.relationship.type) {
            case 'Unit':
              this.lockAndLoad(false)
              this.credits = parseFloat(this.relationship.credits)
              break
            case 'Major':
              this.item = await Curriculum.getMajor(relationship.code, relationship.courseLoopVersionNumber, this.includeDraftVersions)
              break
            case 'Co-Major':
              this.item = await Curriculum.getCoMajor(relationship.code, relationship.courseLoopVersionNumber, this.includeDraftVersions)
              break
            case 'Minor':
              this.item = await Curriculum.getMinor(relationship.code, relationship.courseLoopVersionNumber, this.includeDraftVersions)
              break
            default:
            //console.log(this.relationship.type + " clicked");
          }
        } catch (err) {
          this.error = err
        }

        // //attach relationships / structure
        if (this.isVirtual && this.parentLocation) {
          // attach relationships to parent
          this.addRelationships({
            location: this.parentLocation,
            relationships: [this.relationship]
          })
        }

        if (this.structure) {
          // update structure
          this.attachStructure({
            location: this.location,
            structure: this.structure
          })
        }

        this.lockAndLoad(false)
      } else {
        this.item = null

        this.detachStructure(this.location)
        // if relationship is virtual
        // i.e. loaded because of searched, we'll need to remove it from the current container
        if (this.isVirtual && this.parentLocation) {
          // attach relationships to parent
          this.removeRelationships({
            location: this.parentLocation,
            relationships: [this.relationship]
          })
        }
      }
    },
    viewUnit() {
      this.$store.dispatch('userJourney/viewUnit', {
        relationship: this.relationship,
        location: this.location
      })
    },
    setChildrenLoaded() {
      this.childrenLoaded = true
    },
    async updateUnitDetail() {
      //adding lookup for units that have not yet been loaded from the curriculum
      if (this?.selectedPreferences?.studentType === 'Transnational' && this.type == 'Unit') {
        //check to see if we can return availability via the cached offerings...
        if (this.relationship?.raw?.offerings) {
          //check place of residence
          const placeOfResidence = this.selectedPreferences.placeOfResidence
          this.available = this.relationship.raw.offerings.some((x) => x.location.id === placeOfResidence) === true
          return
        }

        //if we cannot, we will need to load the unit details to determine if it is available.
        if (this.relationship.unit === undefined) {
          this.loadingUnitDetail = true
          try {
            this.relationship.unit = await Curriculum.getUnit(this.relationship.code, this.relationship.version, this.includeDraftVersions)
          } catch (err) {
            this.error = err
          }
          this.loadingUnitDetail = false
        }

        //check place of residence
        const placeOfResidence = this.selectedPreferences.placeOfResidence
        this.available = this?.relationship?.unit?.offerings?.some((x) => x.location.id === placeOfResidence) === true
      }
    }
  },
  async mounted() {
    // update credits based on transaction log at this point in time
    this.credits = this.totalCredits(this.location)
    await this.updateUnitDetail()

    // subscribe to any credit mutations related to me
    if (!this.unsubscribeCreditEvents) {
      this.unsubscribeCreditEvents = this.$store.subscribeAction((action, state) => {
        this.credits = creditChangeCalculator(action.type, action.payload, this.location, this.credits)
      })
    }
  },
  beforeDestroy() {
    if (this.unsubscribeCreditEvents) {
      this.unsubscribeCreditEvents()
      this.unsubscribeCreditEvents = null
    }
  },
  watch: {},
  components: { SlideOutRelationshipElectiveAlert, SlideOutStructure: () => import('./SlideOutStructure.vue') }
}
</script>
